<template>
  <v-col cols="12">
    <div class="mb-4 questionText d-flex align-start">
      <div
        v-if="validated && invalid"
        class="red darken-2 rounded px-2 text-capitalize mr-3 flex-shrink-0 mt-1"
      >
        <v-icon class="white--text">$close</v-icon>
      </div>
      <template v-else>
        <div class="mr-3" v-if="hasQuestionNumber">
          {{ question.questionNumber }}
        </div>
      </template>
      <div>
        <survey-question-text :text="question.text"></survey-question-text>
        <div
          v-if="validated && invalid"
          class="text-caption error--text font-weight-bold"
        >
          {{ errors[0] }}
        </div>
        <div v-else class="text-caption grey--text">{{ hint }}</div>
      </div>
    </div>

    <table class="w-100 stripped">
      <tr v-for="child in question.children" :key="child.key">
        <td class="pl-2">
          <v-checkbox
            :value="optionSelected(child.key)"
            @change="(val) => setKeyPair(child.key, val === true)"
            :label="child.text"
            hide-details
            class="mt-0 mb-1"
            @click.stop
          >
          </v-checkbox>
        </td>
      </tr>
    </table>
  </v-col>
</template>

<style>
.questionText {
  font-size: 1.1rem;
}
</style>

<script>
import Vue from "vue";
import {
  getQuestionSetting,
  questionSettings,
  showQuestion,
} from "../constants/Survey";
import SurveyQuestionText from "./Survey.Question.Text.vue";
import { isEmptyOrSpaces } from "../utilities/dataUtilities";

export default {
  name: "Checklist",
  components: { SurveyQuestionText },
  data: () => ({
    innerValue: [],
    validated: false,
  }),
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    question: {
      type: Object,
    },
  },
  computed: {
    hasQuestionNumber() {
      return (
        this.question.questionNumber && this.question.questionNumber.length > 0
      );
    },
    required() {
      return (
        getQuestionSetting(
          this.question.settings,
          questionSettings.required.key
        ).value == "true"
      );
    },
    min() {
      return getQuestionSetting(
        this.question.settings,
        questionSettings.min.key
      ).value;
    },
    max() {
      return getQuestionSetting(
        this.question.settings,
        questionSettings.max.key
      ).value;
    },
    private() {
      return getQuestionSetting(
        this.question.settings,
        questionSettings.private.key
      ).value;
    },
    fieldValues() {
      let results = [];

      for (let i = 0; i < this.question.children.length; i++) {
        results.push(
          this.innerValue.find((y) => y.key === this.question.children[i].key)
        );
      }

      return results;
    },
    valid() {
      return (
        (this.required && this.completed) ||
        (this.started && this.completed) ||
        (!this.started && !this.required)
      );
    },
    invalid() {
      return !this.valid;
    },
    started() {
      return this.selectedCount > 0;
    },
    completed() {
      return this.errors.length === 0;
    },
    selectedCount() {
      return this.fieldValues.reduce((agg, x) => {
        agg += x.value ? 1 : 0;
        return agg;
      }, 0);
    },
    errors() {
      let errors = [];

      if (this.required || this.started) {
        if (this.min > 0 && this.selectedCount < this.min) {
          errors.push(`Must select at least ${this.min} option(s).`);
        }

        if (this.max > 0 && this.selectedCount > this.max) {
          errors.push(`Cannot select more than ${this.max} option(s).`);
        }
      }

      return errors;
    },
    visible() {
      return showQuestion(this.question, this.innerValue);
    },
    hint() {
      if (this.min == this.max) return `Select ${this.min} from below.`;
      if (this.min > 0 && this.max > 0)
        return `Select between ${this.min} and ${this.max} from below.`;
      else if (this.min > 0) return `Select at least ${this.min} from below.`;
      else if (this.max > 0) return `Select upto ${this.max} from below.`;
      else return "Select any that apply.";
    },
  },
  watch: {
    value(val) {
      if (!keyPairArraysAreEqual(val, this.innerValue)) {
        this.innerValue = val;
      }
    },
    innerValue(value, prev) {
      if (!keyPairArraysAreEqual(value, prev)) {
        this.$emit("input", this.innerValue);
      }
    },
  },
  methods: {
    setKeyPair(key, value) {
      let keyPair = { key, value };

      let index = this.innerValue.findIndex((x) => x.key === key);
      if (index > -1) Vue.set(this.innerValue, index, keyPair);
      else this.innerValue.push(keyPair);
    },
    optionSelected(key) {
      let answer = this.innerValue.find((x) => x.key === key);
      return answer?.value ?? false;
    },
    childAnswered(key) {
      return !isEmptyOrSpaces(this.innerValue.find((x) => x.key === key).value);
    },
    validate() {
      this.validated = true;
      return this.valid;
    },
    reset() {
      this.validated = false;
    },
    getQuestionSetting,
  },
  mounted() {
    if (this.value) this.innerValue = this.value;
    if (showQuestion(this.question, this.innerValue)) {
      this.$nextTick().then(() => {
        this.question.children.forEach((x) => {
          this.setKeyPair(x.key, this.optionSelected(x.key));
        });
      });
    }
  },
  destroyed() {
    if (!showQuestion(this.question, this.innerValue)) {
      this.question.children.forEach((x) => this.setKeyPair(x.key, null));
    }
  },
};

function keyPairArraysAreEqual(a, b) {
  return flatten(a) === flatten(b);

  function flatten(arr) {
    return JSON.parse(JSON.stringify(arr));
  }
}
</script>
